import { register } from "./registry";

/**
 * F3: find a document by series + number and open it read-only (06 §1).
 * Shared/Kit/_FindDocument renders the dialog in a <template>, so a closed dialog adds no fields to the page; it is added on first F3.
 */
let bound = false;
register(() => {
  if (bound) return;
  const found = document.getElementById("kit-find-doc-template");
  if (!(found instanceof HTMLTemplateElement)) return;
  const template = found;
  bound = true;
  let dlg: HTMLDialogElement | null = null;

  function create(): HTMLDialogElement {
    if (dlg) return dlg;
    document.body.appendChild(template.content.cloneNode(true));
    const d = document.getElementById("kit-find-doc") as HTMLDialogElement;
    d.addEventListener("keydown", (e) => onDialogKey(d, e));
    dlg = d;
    return d;
  }

  const parts = (d: HTMLDialogElement) => ({
    type: d.querySelector<HTMLSelectElement>("[data-find=type]")!,
    series: d.querySelector<HTMLInputElement>("[data-find=series]")!,
    no: d.querySelector<HTMLInputElement>("[data-find=no]")!,
    error: d.querySelector<HTMLElement>("[data-find=error]")!,
  });

  function open() {
    const d = create();
    const { type, series, no, error } = parts(d);
    // The screen names its document type; its Series field (if any) is the likely series.
    const form = document.querySelector<HTMLElement>("[data-kit-doc-type]");
    // A screen that saves two types (Cash/Bank receipt or payment) names the field that picks one.
    const kind = form?.querySelector<HTMLSelectElement>("[data-kit-doc-kind]")?.value;
    const pageType = [form?.getAttribute("data-kit-doc-type"), kind].filter(Boolean).join("_");
    if (pageType && Array.from(type.options).some(o => o.value === pageType)) type.value = pageType;
    const pageSeries = form?.querySelector<HTMLInputElement | HTMLSelectElement>("[name$='SeriesId' i], [name$='Series' i]");
    const shown = pageSeries instanceof HTMLSelectElement ? pageSeries.selectedOptions[0]?.text ?? "" : pageSeries?.value ?? "";
    series.value = /^[A-Za-z]+\d*$/.test(shown.trim()) ? shown.trim() : "";
    no.value = "";
    error.textContent = "";
    d.showModal();
    const first = pageType ? series : type;
    first.focus();
    if (first instanceof HTMLInputElement) first.select();
  }

  async function find(d: HTMLDialogElement) {
    const { type, series, no, error } = parts(d);
    error.textContent = "";
    const q = new URLSearchParams({ doc_type: type.value, series: series.value.trim(), no: no.value.trim() });
    const r = await fetch(`/api/v1/documents/find?${q.toString()}`, { headers: { Accept: "application/json" } });
    if (r.ok) {
      const body = await r.json() as { url: string };
      d.close();
      window.location.href = body.url;
      return;
    }
    let message = r.status === 404 ? "Document not found" : "Check the series and number";
    try { const p = await r.json() as { detail?: string; title?: string }; message = p.detail ?? p.title ?? message; } catch { /* keep the default */ }
    error.textContent = message;
    no.focus(); no.select();
  }

  function onDialogKey(d: HTMLDialogElement, e: KeyboardEvent) {
    // The dialog owns its keys: the page's Enter/Esc handlers (keyboard.ts, dirty.ts) must not see them.
    e.stopPropagation();
    if (e.key === "Escape") { e.preventDefault(); d.close(); return; }
    if (e.key !== "Enter" && e.key !== "Tab") return;
    e.preventDefault();
    const { type, series, no } = parts(d);
    const order: HTMLElement[] = [type, series, no];
    const i = order.indexOf(document.activeElement as HTMLElement);
    if (e.shiftKey) { order[Math.max(i - 1, 0)]!.focus(); return; }
    if (document.activeElement === no) { if (e.key === "Enter") void find(d); return; }
    order[i + 1]?.focus();
  }

  document.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key !== "F3" || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    if (dlg?.open) { dlg.close(); return; }
    open();
  });
});
